import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import HeadLogo from "../Assets/Oolook_logo.webp";
import "react-toastify/dist/ReactToastify.css";

const TIMER_DURATION = 4 * 60; // 4 minutes in seconds

const POST_AUTH_REDIRECT_KEY = "post_auth_redirect";

const isSafeInternalRedirect = (value) => {
if (!value) return false;
return value.startsWith("/") && !value.startsWith("//");
};

const getPostAuthRedirect = (fallback = "/") => {
const redirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
return isSafeInternalRedirect(redirect) ? redirect : fallback;
};

export default function VerifyOtp() {
  const [otp, setOtp] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const navigate = useNavigate();
  const email = localStorage.getItem("Email");

  const startTimer = useCallback(() => {
    setTimeLeft(TIMER_DURATION);
  }, []);
  
  // Redirect if email is not found
  useEffect(() => {
    if (!email) {
      toast.error("No email found. Please sign up again.");
      navigate('/signup');
    }
  }, [email, navigate]);

  // Countdown timer logic
  useEffect(() => {
    if (timeLeft === 0) return;
    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const handleVerify = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.");
      return;
    }
    if (timeLeft === 0) {
      toast.error("OTP has expired. Please request a new one.");
      return;
    }

    setVerifyLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/user/verify-otp`, { email, otp });
      toast.success("Email Verified! Welcome to Oolook.");
      
      const { token, email_id } = res.data.data;
      localStorage.setItem("user_Token", token);
      localStorage.setItem("Email", email_id);
      
      window.location.href = "/select-plan";

    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_API}/user/resend-otp`, { email });
      toast.success("A new OTP has been sent.");
      startTimer(); // Restart the timer
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="auth-card auth-shell auth-verification-shell">
        <img src={HeadLogo} alt="Oolook Logo" className="mx-auto h-12 w-auto mb-6" />
        <h2 className="text-center text-2xl font-bold text-neutral-dark mb-2">Check Your Email</h2>
        <p className="text-center text-sm text-neutral-muted mb-6">
          We've sent a 6-digit code to <b className="text-neutral-dark">{email}</b>.
        </p>

        <div className="auth-otp-field">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="------"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
            maxLength={6}
            className="form-input text-center tracking-[1rem] text-2xl font-mono"
            aria-label="6-digit verification code"
          />
        </div>

        {timeLeft > 0 ? (
          <p className="auth-otp-timer">
            Code expires in <strong>{formatTime(timeLeft)}</strong>
          </p>
        ) : (
          <p className="auth-otp-timer auth-otp-expired">
            The code has expired. Request a new one to continue.
          </p>
        )}

        <button
          onClick={handleVerify}
          disabled={verifyLoading || timeLeft === 0}
          className="btn-primary"
        >
          {verifyLoading ? "Verifying..." : "Verify Account"}
        </button>

        <div className="text-center mt-4">
          <button
            onClick={handleResend}
            disabled={resendLoading || timeLeft > 0}
            className="link text-sm disabled:text-neutral-muted disabled:no-underline disabled:cursor-not-allowed"
          >
            {resendLoading ? "Sending..." : "Didn't get a code? Resend"}
          </button>
        </div>
      </div>
    </div>
  );
}